/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as variables from 'pubweb-smokey/dist/components/GridSystem/_vars_widths';
import { isMobile } from 'pubweb-smokey/dist/utils/utils';

import { formatNumberWithCommas } from 'pubweb-smokey/dist/utils/formatters';
import { modelNameTransformer } from 'pubweb-smokey/dist/utils/modelNameTransformer';
import Button from '@components/Shared/Buttons';
import FavoritingHeart from '@components/Shared/Favoriting/FavoritingHeart';
import Bed from 'pubweb-smokey/dist/images/svg/iconography-16x16/bed.svg';
import Bath from 'pubweb-smokey/dist/images/svg/iconography-16x16/bath.svg';
import Home from 'pubweb-smokey/dist/images/svg/iconography-16x16/home.svg';
import CubeOpen from 'pubweb-smokey/dist/images/svg/iconography-16x16/cube-open.svg';

import { HomeCardDetailedStyles } from './HomeCardDetailed.styled';

const HomeCardDetailed = ({
  cardLink,
  nextJSLink,
  cardLinkTarget,
  ctaText,
  handleCtaClick,
  handleFavoriteClick,
  homeObject,
  image,
  imgComponent,
  imageQueryString,
  images,
  isFavorited,
  desktopOrientation = 'vertical',
  showFavoritingHeart = false,
  showPricing = true,
  showStockIndicator = false,
  showUnavailableIndicator = false,
  showVirtualTour = false,
  className,
  onVirtualTourClick,
  linkComponent,
}) => {
  const [viewMore, setViewMore] = useState(false);

  if (!imageQueryString) {
    imageQueryString = '?width=500';
  }

  const getTags = (tags) => {
    let returnTags;
    const defaultTagCount = isMobile(variables.desktop_breakpoint) ? 4 : 6;

    if (viewMore || tags.length <= defaultTagCount) {
      returnTags = tags;
    } else {
      returnTags = tags.slice(0, defaultTagCount - 1);
    }

    const tagComponents = returnTags.map((tag, index) => {
      return (
        <p key={'tag-' + index} className="home-tag caption">
          {tag}
        </p>
      );
    });

    if (tags.length > defaultTagCount) {
      tagComponents.push(
        <a
          key="tag-toggle"
          className="caption"
          onClick={(e) => e.preventDefault() | setViewMore(!viewMore)}
          href="#"
        >
          View {viewMore ? 'less' : tags.length - returnTags.length + ' more'}
        </a>
      );
    }

    return tagComponents;
  };

  const renderLink = (
    renderDescription,
    tabIndex = '0',
    linkClasses = 'h3 home-name'
  ) => {
    const safeCardLink = (cardLink || nextJSLink || '').toLowerCase();

    if (safeCardLink) {
      if (
        safeCardLink.indexOf('http://') === 0 ||
        safeCardLink.indexOf('https://') === 0
      ) {
        return (
          <a
            tabIndex={tabIndex}
            className={`${
              className ? className : 'home-card-link'
            } ${linkClasses}`}
            target={cardLinkTarget || '_blank'}
            href={cardLink || nextJSLink}
          >
            {renderDescription}
          </a>
        );
      } else {
        if (nextJSLink) {
          return React.createElement(
            linkComponent,
            {
              href: nextJSLink,
              passHref: true,
              legacyBehavior: true,
            },
            <a
              className={`${
                className ? className : 'home-card-link'
              } h3 home-name`}
              tabIndex={tabIndex}
            >
              {renderDescription}
            </a>
          );
        } else if (cardLink) {
          return React.createElement(
            linkComponent,
            {
              className: `${
                className ? className : 'home-card-link'
              } h3 home-name`,
              to: cardLink,
            },
            renderDescription
          );
        }
      }
    }

    return modelNameTransformer(homeObject.modelDescription);
  };

  const imageSection = (
    <>
      {!images && image}
      {images &&
        !image &&
        (imgComponent ? (
          React.createElement(imgComponent, {
            src: images[0].reference + imageQueryString,
            alt:
              homeObject && homeObject.modelDescription
                ? modelNameTransformer(homeObject.modelDescription)
                : '',
            layout: 'fill',
          })
        ) : (
          <img
            src={images[0].reference + imageQueryString}
            alt={
              homeObject && homeObject.modelDescription
                ? modelNameTransformer(homeObject.modelDescription)
                : ''
            }
          />
        ))}
    </>
  );

  return (
    <HomeCardDetailedStyles
      className={'home-card-detailed ' + desktopOrientation}
    >
      <div className="home-image">
        <div
          className="home-image-container"
          tabIndex="0"
          onKeyUp={(ev) => {
            if (
              typeof window !== 'undefined' &&
              (cardLink !== '' || nextJSLink !== '') &&
              ev.keyCode === 13
            ) {
              window.location.href = cardLink || nextJSLink;
            }
          }}
        >
          {renderLink(imageSection, '-1')}

          {showVirtualTour &&
            homeObject.hasVirtualTour &&
            homeObject.virtualTourReference && (
              <a
                className="home-virtual-tour label large"
                href="#"
                onClick={onVirtualTourClick}
              >
                {' '}
                <CubeOpen /> View 3D Tour
              </a>
            )}
          {showFavoritingHeart && (
            <FavoritingHeart
              onClick={handleFavoriteClick}
              isFavorited={isFavorited}
            />
          )}
        </div>
      </div>
      <div className="home-details">
        {showStockIndicator && homeObject.numberInStock > 0 && (
          <div className="stock-indicator label">In Stock</div>
        )}
        {showUnavailableIndicator && (
          <div className="unavailable-indicator label">
            Unavailable in your area
          </div>
        )}
        <div className="home-header">
          <div className="home-header-text">
            {renderLink(modelNameTransformer(homeObject.modelDescription))}
            {showPricing && (
              <p className="home-price">
                ${formatNumberWithCommas(homeObject.lowPrice)}
                's* Before Options
              </p>
            )}
          </div>
          <div>
            {ctaText && handleCtaClick && (
              <Button
                isCompact={true}
                className="home-header-cta"
                automationId="home-header-cta"
                onClick={handleCtaClick}
              >
                {ctaText}
              </Button>
            )}
          </div>
        </div>
        <div className="home-features">
          <Bed />
          <p className="caption">
            {homeObject.beds} bed{homeObject.beds !== 1 ? 's' : ''}
          </p>
          <Bath />
          <p className="caption">
            {homeObject.baths} bath
            {homeObject.baths !== 1 ? 's' : ''}
          </p>
          <Home />
          <p className="caption">
            {formatNumberWithCommas(homeObject.maxSquareFeet)} sq. ft.
          </p>
        </div>
        {homeObject.tags?.length > 0 && (
          <div className={'home-tags ' + (viewMore ? 'more' : '')}>
            <label className="label large">Features</label>
            <div className="home-tags-items">{getTags(homeObject.tags)}</div>
          </div>
        )}
        {ctaText && handleCtaClick && (
          <Button
            className="home-details-cta"
            automationId="home-details-cta"
            fillContainer={true}
            onClick={handleCtaClick}
          >
            {ctaText}
          </Button>
        )}
      </div>
    </HomeCardDetailedStyles>
  );
};

HomeCardDetailed.propTypes = {
  cardLink: PropTypes.string,
  nextJSLink: PropTypes.string,
  cardLinkTarget: PropTypes.string,
  ctaText: PropTypes.string,
  desktopOrientation: PropTypes.oneOf(['vertical', 'horizontal']),
  handleFavoriteClick: PropTypes.func,
  handleCtaClick: PropTypes.func,
  homeObject: PropTypes.shape({
    numberInStock: PropTypes.number,
    modelId: PropTypes.number,
    modelNumber: PropTypes.string,
    modelDescription: PropTypes.string,
    baths: PropTypes.number,
    beds: PropTypes.number,
    maxSquareFeet: PropTypes.number,
    lowPrice: PropTypes.number,
    virtualTourReference: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    hasVirtualTour: PropTypes.bool,
  }),
  imgComponent: PropTypes.object,
  imageQueryString: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.shape({ reference: PropTypes.string })),
  isFavorited: PropTypes.bool,
  isInStock: PropTypes.bool,
  showFavoritingHeart: PropTypes.bool,
  showStockIndicator: PropTypes.bool,
  showUnavailableIndicator: PropTypes.bool,
  showPricing: PropTypes.bool,
  className: PropTypes.string,
  onVirtualTourClick: PropTypes.func,
  image: PropTypes.any,
  showVirtualTour: PropTypes.bool,
  linkComponent: PropTypes.object,
};

export default HomeCardDetailed;
